import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../../components/ViewContainer";
import DashboardGrid from "../../../components/grid/DashboardGrid";
import Panel from "../../../components/grid/Panel";
import KpiStat from "../../../components/kpi/KpiStat";
import BarList from "../../../components/charts/BarList";
import DataTable from "../../../components/DataTable";
import CountUp from "../../../components/CountUp";
import { formatInt } from "../../../utils/format";
import { blueScaleByRank } from "../../../utils/colorScale";
import { brand } from "../../../themes/theme";

import dataQualityData from "../../../data/pharma/dataQuality.json";
import subgroupsData from "../../../data/pharma/subgroups.json";

// Color por tipo de fuente (mismo criterio que Subgroups > Characterization,
// y que Datos y cohorte en clinica): cuestionario/tracker estructurado en
// gris neutro (la base), contenido humano activo (notas, chat, videollamada,
// comunidad) en azul, senal pasiva de la app (GPS, sensores, uso de
// pantalla/app, ejercicios guiados o de IA) en naranja.
const SOURCE_TYPE_COLORS = {
    questionnaire: { bg: brand.gray100, border: brand.gray200, color: brand.gray600 },
    selfReport: { bg: "rgba(50,72,202,0.08)", border: "rgba(50,72,202,0.28)", color: brand.primary },
    passive: { bg: "rgba(255,130,57,0.09)", border: "rgba(255,130,57,0.32)", color: brand.accentMedium },
};

// Mismos 3 tipos, pero para relleno de barra (BarList): las 3 mas claras que
// el tono solido de las cajitas de Sources (primaryLight/accentLight en vez
// de primary/accentMedium; gris a medio camino entre gray200 y gray500).
const COMPLETENESS_BAR_COLORS = {
    questionnaire: "#9CA3AF",
    selfReport: brand.primaryLight,
    passive: brand.accentLight,
};

// Modulo 1 — Data captured and quality of the data.
function DataQualityView() {
    const { t } = useTranslation();
    const d = dataQualityData;
    const subgroups = subgroupsData.items;

    const completenessItems = d.completenessBySource.map((s) => ({
        label: s.label,
        pct: s.pct,
        value: `${s.pct}%`,
        color: COMPLETENESS_BAR_COLORS[s.type],
    }));

    // Todos los valores de abandono son bajos (4-19%): a escala absoluta 0-100
    // las barras quedan casi identicas entre si (todas son astillas finas) y
    // la diferencia real entre subgrupos deja de verse. Se reescala la barra
    // al maximo del propio grupo (el numero mostrado sigue siendo el real).
    const dropoutMax = Math.max(...d.dropoutBySubgroup.map((row) => row.pct));
    const dropoutColors = blueScaleByRank(d.dropoutBySubgroup.map((row) => row.pct));
    const dropoutItems = d.dropoutBySubgroup.map((row, i) => {
        const sg = subgroups.find((s) => s.id === row.subgroupId);
        return { label: sg?.name || row.subgroupId, pct: (row.pct / dropoutMax) * 100, value: `${row.pct}%`, color: dropoutColors[i] };
    });

    return (
        <ViewContainer title={t("views.pharmaDataQuality.title")} description={t("views.pharmaDataQuality.description")}>
            <DashboardGrid gap={2} rowGap={2.5}>
                <Panel span={12} dense>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2, sm: 3 }, flexWrap: "wrap" }}>
                        <Kpi first label={t("views.pharmaDataQuality.kpi.sourcesLabel")} value={d.kpis.dataSources} hint={t("views.pharmaDataQuality.kpi.sourcesHint")} />
                        <Kpi tone="primary" label={t("views.pharmaDataQuality.kpi.markersLabel")}
                            value={<CountUp to={d.kpis.markersCaptured} format={formatInt} />}
                            hint={t("views.pharmaDataQuality.kpi.markersHint", { catalogue: formatInt(d.kpis.markersCatalogue) })} />
                        <Kpi label={t("views.pharmaDataQuality.kpi.instrumentsLabel")} value={d.kpis.instrumentCount} hint={t("views.pharmaDataQuality.kpi.instrumentsHint")} />
                        <Kpi label={t("views.pharmaDataQuality.kpi.cohortLabel")} value={<CountUp to={d.kpis.cohortN} format={formatInt} />} hint={t("views.pharmaDataQuality.kpi.cohortHint")} />
                        <Kpi tone="accent" label={t("views.pharmaDataQuality.kpi.dropoutLabel")} value={<CountUp to={d.kpis.dropoutPct} suffix="%" />} hint={t("views.pharmaDataQuality.kpi.dropoutHint")} />
                    </Box>
                </Panel>

                <Panel span={{ xs: 12, md: 5 }} dense title={t("views.pharmaDataQuality.captureCadenceTitle")}>
                    <DataTable
                        dense
                        columns={[
                            { key: "label", label: "—", width: "140px" },
                            { key: "detail", label: "—" },
                        ]}
                        rows={d.captureCadence.map((c) => ({ label: c.label, detail: c.detail }))}
                    />
                </Panel>

                <Panel span={{ xs: 12, md: 7 }} dense title={t("views.pharmaDataQuality.completenessTitle")} subtitle={t("views.pharmaDataQuality.completenessHint")}>
                    <BarList items={completenessItems} />
                </Panel>

                <Panel span={{ xs: 12, md: 7 }} dense title={t("views.pharmaDataQuality.instrumentBatteryTitle")}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {["questionnaire", "selfReport", "passive"].map((type) => {
                            const items = d.instrumentBattery.filter((item) => item.type === type);
                            if (!items.length) return null;
                            const c = SOURCE_TYPE_COLORS[type];
                            return (
                                <Box key={type} sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {items.map((item) => (
                                        <Box
                                            key={item.name}
                                            sx={{
                                                fontSize: 11.5,
                                                bgcolor: c.bg,
                                                border: "1px solid",
                                                borderColor: c.border,
                                                color: c.color,
                                                borderRadius: "6px",
                                                px: 1.1,
                                                py: 0.4,
                                            }}
                                        >
                                            {item.name}
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })}
                    </Box>
                </Panel>

                <Panel span={{ xs: 12, md: 5 }} dense title={t("views.pharmaDataQuality.dropoutBySubgroupTitle")} subtitle={t("views.pharmaDataQuality.dropoutBySubgroupHint")}>
                    <BarList items={dropoutItems} />
                </Panel>
            </DashboardGrid>
        </ViewContainer>
    );
}

function Kpi({ label, value, hint, tone, first }) {
    return (
        <Box sx={{ flex: { xs: "0 1 auto", sm: "1 1 150px" }, ...(!first && { borderLeft: { sm: "1px solid" }, borderColor: { sm: "divider" }, pl: { sm: 3 } }) }}>
            <KpiStat label={label} value={value} tone={tone} hint={hint} compact />
        </Box>
    );
}

export default DataQualityView;
